import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { rand, categoryMeta } from './financeApi';

const LABELS = { month: 'this month', quarter: 'this quarter', six_months: 'in the last 6 months', year: 'this year', last_year: 'in the past year' };
const TITLES = { month: 'This month', quarter: 'This quarter', six_months: 'Last 6 months', year: 'This year', last_year: 'Past year' };

const Breakdown = ({ p, periodKey }) => {
  const top = p.breakdown[0];
  return (
    <div className="space-y-3">
      {top && (
        <p className="text-base md:text-lg font-heading" data-testid="money-flow-headline">
          {p.income > 0
            ? <>{categoryMeta(top.category).label} took <strong className="text-amber-700">{Math.round(top.pct_of_income)}%</strong> of your income {LABELS[periodKey]}.</>
            : <>{categoryMeta(top.category).label} was your biggest cost {LABELS[periodKey]} ({rand(top.amount)}). No parent payments recorded yet.</>}
        </p>
      )}
      <ul className="space-y-2" data-testid="money-flow-list">
        {p.breakdown.map((b) => {
          const meta = categoryMeta(b.category);
          const Icon = meta.icon;
          const width = p.income > 0 ? Math.min(100, b.pct_of_income) : (b.amount / p.total) * 100;
          return (
            <li key={b.category} className="text-sm" data-testid={`money-flow-${b.category}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2"><span className={`w-7 h-7 rounded-lg flex items-center justify-center ${meta.color}`}><Icon className="w-4 h-4" /></span>{meta.label}</span>
                <span className="font-medium">{rand(b.amount)}{b.pct_of_income != null && <span className="text-muted-foreground"> · {b.pct_of_income}%</span>}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full rounded-full bg-amber-400 transition-[width] duration-700" style={{ width: `${width}%` }} /></div>
            </li>
          );
        })}
        {p.breakdown.length === 0 && <li className="text-sm text-muted-foreground">No expenses {LABELS[periodKey]}.</li>}
      </ul>
    </div>
  );
};

const PerVehicle = ({ list }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="money-flow-per-vehicle">
      <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Vehicle</th><th>Income</th><th>Expenses</th><th>Left</th><th>Biggest cost</th></tr></thead>
      <tbody>
        {list.map((v) => (
          <tr key={v.vehicle_id} className="border-t" data-testid={`money-flow-vehicle-${v.registration_number}`}>
            <td className="py-2 font-medium">{v.registration_number}</td>
            <td className="text-emerald-700">{rand(v.income)}</td>
            <td>{rand(v.expenses)}</td>
            <td className={v.left < 0 ? 'text-red-700 font-semibold' : 'font-semibold'}>{rand(v.left)}</td>
            <td className="text-xs">{v.breakdown[0] ? `${categoryMeta(v.breakdown[0].category).label}${v.breakdown[0].pct_of_income != null ? ` (${v.breakdown[0].pct_of_income}%)` : ''}` : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const MoneyFlow = ({ reports }) => {
  const [key, setKey] = useState('month');
  const p = reports.periods[key];
  return (
    <section className="rounded-3xl bg-white border p-4 sm:p-6 space-y-5" data-testid="money-flow">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-700" /> Where the money goes</h3>
        <div className="flex flex-wrap gap-1">
          {Object.keys(reports.periods).map((k) => (
            <button key={k} type="button" onClick={() => setKey(k)} data-testid={`money-flow-period-${k}`}
              className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${k === key ? 'bg-emerald-900 text-white border-emerald-900' : 'hover:bg-muted/40'}`}>{TITLES[k]}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="rounded-2xl bg-emerald-50 p-3"><p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Money in</p><p className="text-base sm:text-2xl font-heading font-bold whitespace-nowrap text-emerald-800" data-testid="money-flow-income">{rand(p.income)}</p></div>
        <div className="rounded-2xl bg-amber-50 p-3"><p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" /> Expenses</p><p className="text-base sm:text-2xl font-heading font-bold whitespace-nowrap text-amber-800" data-testid="money-flow-expenses">{rand(p.total)}</p></div>
        <div className={`rounded-2xl p-3 ${p.left < 0 ? 'bg-red-50' : 'bg-slate-900 text-white'}`}><p className={`text-xs ${p.left < 0 ? 'text-muted-foreground' : 'text-slate-300'}`}>Left after expenses</p><p className={`text-base sm:text-2xl font-heading font-bold whitespace-nowrap ${p.left < 0 ? 'text-red-700' : ''}`} data-testid="money-flow-left">{rand(p.left)}</p></div>
      </div>
      <Breakdown p={p} periodKey={key} />
      {p.per_vehicle && p.per_vehicle.length > 0 && <PerVehicle list={p.per_vehicle} />}
    </section>
  );
};
